/* eslint-disable no-nested-ternary */
/* eslint-disable no-param-reassign */
import {
  GetOrders200Response,
  SideResponse,
} from "../../../codegen-api";
import { COLORS } from "../../../constants/design/colors";
import { MARK_PRICE_COLLAR } from "../../../constants/precision/form";
import { IPriceSize } from "../../../interfaces/Orderbook";
import {
  getCumulativeValue,
  isOrderPriceAllowed,
} from "../../../utils/orderbook";
import { knownPrice } from "../../../utils/unknownNumbers";
import { OrderbookFilterEnum } from "../../Perps/TradeOrderbookSection/shared";
import { RowUpdater } from "./style";
import { OrderbookRow } from "./OrderbookRow";

/**
 * The protection band is centred on the mark and widened by the index. With
 * either unknown there is no band to draw: a barrier around a price of 0 would
 * mark ordinary levels as out of range.
 */
export const knownCollarPrices = (
  markPrice?: number,
  indexPrice?: number
): { mark: number; index: number } | undefined => {
  const mark = knownPrice(markPrice);
  const index = knownPrice(indexPrice);
  return mark === undefined || index === undefined ? undefined : { mark, index };
};

interface BaseCellsParams {
  filter?: OrderbookFilterEnum;
  markPrice?: number;
  indexPrice?: number;
  indexPriceCollar: number;
  showValueInUSD?: boolean;
  accountOrders: GetOrders200Response[];
  orderbookTicksize?: number | undefined;
  instrumentId?: string;
  onRowClick: (price: string, size: string, side: SideResponse) => void;
  showIV: boolean;
  sizeDecimals: number;
  priceDecimals: number;
  rowHeight?: string;
  protectionBarrierContent: (type: "bid" | "ask") => JSX.Element | undefined;
}

interface AskCellsParams extends BaseCellsParams {
  sortedAsks: IPriceSize[];
}

interface BidCellsParams extends BaseCellsParams {
  sortedBids: IPriceSize[];
}

export function renderAskCells({
  filter,
  sortedAsks,
  markPrice,
  indexPrice,
  indexPriceCollar,
  showValueInUSD,
  accountOrders,
  orderbookTicksize,
  instrumentId,
  onRowClick,
  showIV,
  sizeDecimals,
  priceDecimals,
  rowHeight,
  protectionBarrierContent,
}: AskCellsParams) {
  if (filter !== OrderbookFilterEnum.BIDS) {
    // Checks for the ask IV against mark's ask IV
    const collar = knownCollarPrices(markPrice, indexPrice);
    const protectionBarrierIndex = collar
      ? sortedAsks.findIndex(
          (ask) =>
            !!Number(ask[0]) &&
            !isOrderPriceAllowed(
              Number(ask[0] || 0),
              collar.mark,
              collar.index,
              MARK_PRICE_COLLAR,
              indexPriceCollar
            )
        )
      : -1;
    //@ts-ignore
    const rows = sortedAsks.map(([price, size, iv], index) => {
      const isNotLastRow =
        sortedAsks.length > 1 ? index !== sortedAsks.length - 1 : true;
      const hasBarrier = protectionBarrierIndex === index && isNotLastRow;

      // Sizes is price * contracts if show values in USD
      const realSize = showValueInUSD
        ? Number(size) * Number(price)
        : Number(size);
      const allSizes = sortedAsks.map((v) =>
        showValueInUSD ? Number(v[0]) * Number(v[1]) : Number(v[1])
      );
      const totalSize = allSizes.reduce((prev, s) => prev + Number(s), 0);
      const cumulative = getCumulativeValue(allSizes, index);

      const accountSize = (accountOrders || [])
        .filter((order) => {
          const priceUpperBound = Number(price) + (orderbookTicksize || 0);
          // If theres a tick size, we need to check if the order falls in the same level
          const orderWithinLevel = orderbookTicksize
            ? Number(order.price) >= Number(price) &&
              Number(order.price) < priceUpperBound
            : order.price === price;
          return order.instrument_name === instrumentId && orderWithinLevel;
        })
        .reduce((acc, current) => acc + Number(current.amount), 0);

      const key = Number(price) ? `${price}-${size}` : index;

      return (
        <RowUpdater
          key={key}
          onClick={() => onRowClick(price, size, SideResponse.Sell)}
          aria-hidden="true"
          style={{
            pointerEvents: Number(price) ? "auto" : "none",
          }}
        >
          <OrderbookRow
            index={Math.max(sortedAsks.length - index - 1, 0)}
            price={price}
            iv={showIV ? iv : undefined}
            size={String(realSize)}
            sizeIsUSD={!!showValueInUSD}
            sizeDecimals={sizeDecimals}
            priceDecimals={priceDecimals}
            accountSize={accountSize}
            totalSize={totalSize}
            cumulativeSize={cumulative}
            color={COLORS.down.four}
            textColor={COLORS.down.one}
            type="ask"
            rowHeight={rowHeight}
          />
          {hasBarrier && Number(size) > 0 && protectionBarrierContent("ask")}
        </RowUpdater>
      );
    });
    return rows.reverse();
  }
  return undefined;
}

export function renderBidCells({
  filter,
  sortedBids,
  markPrice,
  indexPrice,
  indexPriceCollar,
  showValueInUSD,
  accountOrders,
  orderbookTicksize,
  instrumentId,
  onRowClick,
  showIV,
  sizeDecimals,
  priceDecimals,
  rowHeight,
  protectionBarrierContent,
}: BidCellsParams) {
  if (filter !== OrderbookFilterEnum.OFFERS) {
    const collar = knownCollarPrices(markPrice, indexPrice);
    const protectionBarrierIndex = collar
      ? sortedBids.findIndex(
          (bid) =>
            !!Number(bid[0]) &&
            !isOrderPriceAllowed(
              Number(bid[0] || 0),
              collar.mark,
              collar.index,
              MARK_PRICE_COLLAR,
              indexPriceCollar
            )
        )
      : -1;
    //@ts-ignore
    return sortedBids.map(([price, size, iv], index) => {
      const isNotLastRow =
        sortedBids.length - 1 > 1 ? index !== sortedBids.length - 1 : true;
      const hasBarrier = protectionBarrierIndex === index && isNotLastRow;

      // Sizes is price * contracts if show values in USD
      const realSize = showValueInUSD
        ? Number(size) * Number(price)
        : Number(size);
      const allSizes = sortedBids.map((v) =>
        showValueInUSD ? Number(v[0]) * Number(v[1]) : Number(v[1])
      );
      const totalSize = allSizes.reduce((prev, s) => prev + Number(s), 0);
      const cumulative = getCumulativeValue(allSizes, index);

      const accountSize = (accountOrders || [])
        .filter((order) => {
          const priceUpperBound = Number(price) + (orderbookTicksize || 0);
          // If theres a tick size, we need to check if the order falls in the same level
          const orderWithinLevel = orderbookTicksize
            ? Number(order.price) >= Number(price) &&
              Number(order.price) < priceUpperBound
            : order.price === price;
          return order.instrument_name === instrumentId && orderWithinLevel;
        })
        .reduce((acc, current) => acc + Number(current.amount), 0);

      const key = Number(price) ? `${price}-${size}` : index;

      return (
        <RowUpdater
          key={key}
          onClick={() => onRowClick(price, size, SideResponse.Buy)}
          aria-hidden="true"
          style={{
            pointerEvents: Number(price) ? "auto" : "none",
          }}
        >
          {hasBarrier && Number(size) > 0 && protectionBarrierContent("bid")}
          <OrderbookRow
            index={Math.max(sortedBids.length - index - 1, 0)}
            price={price}
            iv={showIV ? iv : undefined}
            size={String(realSize)}
            sizeIsUSD={!!showValueInUSD}
            sizeDecimals={sizeDecimals}
            priceDecimals={priceDecimals}
            accountSize={accountSize}
            totalSize={totalSize}
            cumulativeSize={cumulative}
            color={COLORS.up.four}
            textColor={COLORS.up.one}
            type="bid"
            rowHeight={rowHeight}
          />
        </RowUpdater>
      );
    });
  }
  return undefined;
}
